import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import PipelineProfileCloud, { PipelineProfile } from './PipelineProfileCloud';
import Avatar from './Avatar';
import { getImageUrl } from '../utils/imageUrl';
import {
  formatMbcbReportPeriod,
  getCurrentFilingMonthYear,
  getPreviousMonthMinistryPeriod,
  mbcbReportNamedTitleFromMinistry,
} from '../utils/mbcbReports';
import {
  getFundingPlantersRawStep,
  MBCB_PLANTING_STEP_SCOPE,
  requestMbcbDraftsForPeriod,
  sendPlanterAppInvite,
  type Planter,
} from '../api/plantersService';

function axiosErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    return typeof msg === 'string' ? msg : err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

interface FundingMbcbActionsProps {
  profiles: PipelineProfile[];
  title: string;
  listHref: string;
  accentClassName?: string;
}

const FUNDING_PLANTERS_QUERY_KEY = ['fundingPlantersRawStep'] as const;

const FundingMbcbActions: React.FC<FundingMbcbActionsProps> = ({
  profiles,
  title,
  listHref,
  accentClassName,
}) => {
  const queryClient = useQueryClient();
  const [showAdmin, setShowAdmin] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [invitedIds, setInvitedIds] = useState<Set<number>>(() => new Set());
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const now = useMemo(() => new Date(), []);
  const { month: ministryMonth, year: ministryYear } = useMemo(
    () => getPreviousMonthMinistryPeriod(now),
    [now],
  );
  const { month: filingMonth, year: filingYear } = useMemo(() => getCurrentFilingMonthYear(now), [now]);

  const fundingQuery = useQuery({
    queryKey: FUNDING_PLANTERS_QUERY_KEY,
    queryFn: getFundingPlantersRawStep,
    enabled: expanded,
    staleTime: 60_000,
  });

  const requestDraftsMutation = useMutation({
    mutationFn: () =>
      requestMbcbDraftsForPeriod({
        reportMonth: ministryMonth,
        reportYear: ministryYear,
        plantingStepScope: MBCB_PLANTING_STEP_SCOPE.FUNDING,
      }),
    onSuccess: (data) => {
      setError(null);
      setFeedback(
        `Created ${data.createdCount} draft${data.createdCount === 1 ? '' : 's'} · Skipped ${data.skippedCount} (already existed)`,
      );
      queryClient.invalidateQueries({ queryKey: ['planterPipelineOverview'] });
      queryClient.invalidateQueries({ queryKey: FUNDING_PLANTERS_QUERY_KEY });
    },
    onError: (e) => {
      setFeedback(null);
      setError(axiosErrorMessage(e));
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (planterId: number) => sendPlanterAppInvite(planterId),
    onSuccess: (_data, planterId) => {
      setInvitedIds((prev) => new Set(prev).add(planterId));
      setError(null);
    },
    onError: (e) => {
      setError(axiosErrorMessage(e));
    },
  });

  const onCreateDrafts = async () => {
    setFeedback(null);
    setError(null);
    try {
      const rows = await queryClient.fetchQuery({
        queryKey: FUNDING_PLANTERS_QUERY_KEY,
        queryFn: getFundingPlantersRawStep,
        staleTime: 60_000,
      });
      const n = rows.length;
      const namedTitle = mbcbReportNamedTitleFromMinistry(ministryMonth, ministryYear);
      const ministryLabel = formatMbcbReportPeriod(ministryMonth, ministryYear);
      const ok = window.confirm(
        `Create ${namedTitle} draft MBCB reports (covering ${ministryLabel} ministry) for ${n} funding-stage planter${
          n === 1 ? '' : 's'
        }? Existing drafts for that ministry month are skipped.`,
      );
      if (!ok) return;
      requestDraftsMutation.mutate();
    } catch (e) {
      setError(axiosErrorMessage(e));
    }
  };

  const fundingRows: Planter[] = fundingQuery.data ?? [];

  return (
    <div className="space-y-2">
      <PipelineProfileCloud
        profiles={profiles}
        title={title}
        listHref={listHref}
        accentClassName={accentClassName}
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowAdmin((v) => !v)}
          aria-expanded={showAdmin}
          aria-label={showAdmin ? 'Hide MBCB report tools' : 'Show MBCB report tools'}
          title={showAdmin ? 'Hide MBCB report tools' : 'MBCB report tools'}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {showAdmin && (
        <div className="rounded-lg border border-orange-200/80 bg-white/90 px-3 py-2.5 space-y-2">
          <p className="text-[11px] text-gray-600 leading-snug">
            MBCB bulk drafts go only to <span className="font-medium">funding-stage</span> planters
            (effective step — expired funding dates and post-funding are skipped). Drafts match the usual
            filing window: the report is named for the current calendar month and covers the{' '}
            <span className="font-medium">previous</span> month of ministry. If you already created drafts for the
            wrong ministry month, delete those in admin before creating again.
          </p>
          <button
            type="button"
            onClick={() => void onCreateDrafts()}
            disabled={requestDraftsMutation.isPending}
            className="w-full rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-60"
          >
            {requestDraftsMutation.isPending
              ? 'Creating drafts…'
              : `Create ${filingMonth}/${filingYear} MBCB drafts (covers ${ministryMonth}/${ministryYear} ministry)`}
          </button>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-primary-700 hover:text-primary-900"
          >
            {expanded ? 'Hide account status' : 'Show account status & invites'}
          </button>

          {expanded && (
            <div className="border-t border-gray-100 pt-2">
              {fundingQuery.isLoading && (
                <p className="text-xs text-gray-500">Loading funding-stage planters…</p>
              )}
              {fundingQuery.isError && (
                <p className="text-xs text-red-600">Could not load funding planters.</p>
              )}
              {fundingQuery.isSuccess && fundingRows.length === 0 && (
                <p className="text-xs text-gray-500">No funding-stage planters.</p>
              )}
              {fundingQuery.isSuccess && fundingRows.length > 0 && (
                <ul className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                  {fundingRows.map((p) => {
                    const img = getImageUrl(p.headshotImagePath, { width: 48, height: 48, quality: 80 });
                    const inviteSent = invitedIds.has(p.id);
                    return (
                      <li
                        key={p.id}
                        className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50/80 px-2 py-1.5"
                      >
                        <Link to={`/planters/${p.id}`} className="flex min-w-0 flex-1 items-center gap-2">
                          {img ? (
                            <img
                              src={img}
                              alt=""
                              className="h-9 w-9 flex-shrink-0 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <Avatar name={p.name} size="sm" className="flex-shrink-0" />
                          )}
                          <div className="min-w-0 text-left">
                            <div className="truncate text-sm font-medium text-gray-900">{p.name}</div>
                            {p.churchName ? (
                              <div className="truncate text-xs text-gray-600">{p.churchName}</div>
                            ) : null}
                          </div>
                        </Link>
                        <div className="flex flex-shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
                          {p.hasPlanterAppUserLinked ? (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                              App account linked
                            </span>
                          ) : inviteSent ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                              Invite sent
                            </span>
                          ) : (
                            <>
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                                No app account
                              </span>
                              <button
                                type="button"
                                disabled={inviteMutation.isPending}
                                onClick={() => inviteMutation.mutate(p.id)}
                                className="rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                              >
                                Invite
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {feedback ? <p className="text-xs text-emerald-800">{feedback}</p> : null}
          {error ? <p className="text-xs text-red-700">{error}</p> : null}
        </div>
      )}
    </div>
  );
};

export default FundingMbcbActions;
